/**
 * D15 — Impact.
 *
 * Two sets of figures, both server-computed: the charity's overall totals and
 * this donor's own contributions. Nothing on this screen is a constant.
 */

import { StyleSheet, View } from 'react-native';

import { formatCount, formatImpactCount, formatMoney, formatMonthYear } from '@mfa/domain';
import { colors, radius, space } from '../../theme';
import { Button } from '../../components/common/Button';
import { Card, CardDivider, CardRow } from '../../components/common/Card';
import { EmptyState, ErrorState } from '../../components/feedback/States';
import { Glyph } from '../../components/common/Icon';
import { ScreenShell } from '../../components/layout/ScreenShell';
import { Skeleton } from '../../components/feedback/Skeleton';
import { Text } from '../../components/common/Text';
import { useImpactStats, useMyImpact } from '../../core/api/hooks';
import type { DonorScreenProps } from '../../app/navigation/types';
import { useT } from '../../core/i18n/LanguageProvider';

export function ImpactScreen({ navigation }: DonorScreenProps<'Impact'>) {
  const t = useT();
  const overall = useImpactStats();
  const mine = useMyImpact();

  const tiles = overall.data
    ? [
        { value: formatImpactCount(overall.data.mealsServed), caption: 'Meals Served' },
        { value: formatImpactCount(overall.data.happyDonors), caption: 'Happy Donors' },
        { value: formatImpactCount(overall.data.cities), caption: 'Cities' },
        {
          value: formatImpactCount(overall.data.partnerOrganizations),
          caption: 'Partner Organizations',
        },
      ]
    : [];

  return (
    <ScreenShell
      leading="menu"
      onMenuPress={() => navigation.getParent()?.dispatch({ type: 'OPEN_DRAWER' })}
      onRefresh={() => {
        void overall.refetch();
        void mine.refetch();
      }}
      refreshing={overall.isRefetching || mine.isRefetching}
      testID="screen-impact"
      title={t('Your Impact')}
    >

      <View style={styles.banner}>
        <View style={styles.bannerText}>
          <Text variant="cardTitle" tone="onBrand">{t("You're Making a Difference!")}</Text>
          <Text variant="caption" tone="onBrandMuted">{t('Thank you for helping feed people in need.')}</Text>
        </View>
        <Glyph name="care" size={36} />
      </View>

      <Text variant="sectionTitle" style={styles.heading}>{t('Overall Impact')}</Text>

      {overall.isError ? (
        <ErrorState
          title={t('Statistics unavailable')}
          message={t('We could not load the impact figures.')}
          onRetry={() => void overall.refetch()}
        />
      ) : (
        <View style={styles.grid}>
          {(overall.isLoading ? [0, 1, 2, 3] : tiles).map((item, index) => (
            <View key={index} style={styles.tile}>
              {typeof item === 'number' ? (
                <>
                  <Skeleton width={72} height={26} />
                  <Skeleton width={90} height={12} />
                </>
              ) : (
                <>
                  <Text variant="amount" tone="brand" align="center">
                    {item.value}
                  </Text>
                  <Text variant="caption" tone="secondary" align="center">
                    {t(item.caption)}
                  </Text>
                </>
              )}
            </View>
          ))}
        </View>
      )}

      <Text variant="sectionTitle" style={styles.heading}>{t('Your Contributions')}</Text>

      {mine.isError ? (
        // Not "sign in" any more: a guest is a first-class donor here, and
        // this branch now means the request failed, not that anyone is
        // unidentified.
        <ErrorState
          title={t('Your contributions are unavailable')}
          message={t('We could not load your totals just now.')}
          onRetry={() => void mine.refetch()}
        />
      ) : mine.data && mine.data.totalDonations === 0 ? (
        <EmptyState
          title={t('No donations yet')}
          message={t('Sponsor your first meal and your impact will start building here.')}
          actionLabel={t('Support a Meal')}
          onAction={() => navigation.navigate('DonateMeal')}
        />
      ) : (
        <Card testID="my-contributions">
          {!mine.data ? (
            <View style={styles.loadingRows}>
              <Skeleton height={20} />
              <Skeleton height={20} />
              <Skeleton height={20} />
            </View>
          ) : (
            <>
              <CardRow label={t('Total Donations')} value={formatCount(mine.data.totalDonations)} />
              <CardDivider />
              <CardRow
                label={t('Total People Helped')}
                value={formatCount(mine.data.totalPeopleHelped)}
              />
              <CardDivider />
              <CardRow
                label={t('Total Amount Donated')}
                value={formatMoney(mine.data.totalAmountDonated)}
              />
              {mine.data.since ? (
                <>
                  <CardDivider />
                  <CardRow label={t('Since')} value={formatMonthYear(mine.data.since)} />
                </>
              ) : null}
            </>
          )}
        </Card>
      )}

      <View style={styles.quote}>
        <Text variant="body" tone="brand" align="center">{t("Every meal you sponsor brings hope and happiness to someone's life.")}</Text>
        <Text variant="bodyStrong" tone="brand" align="center">{t('JazakAllah!')}</Text>
      </View>

      <Button
        label={t('Support a Meal')}
        onPress={() => navigation.navigate('DonateMeal')}
        style={styles.cta}
        testID="impact-cta-donate"
      />
    </ScreenShell>
  );
}

const styles = StyleSheet.create({
  banner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.md,
    marginTop: space.lg,
    padding: space.lg,
    borderRadius: radius.lg,
    backgroundColor: colors.bg.brand,
  },
  bannerText: {
    flex: 1,
    gap: space.xxs,
  },
  heading: {
    marginTop: space.xl,
    marginBottom: space.md,
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: space.md,
  },
  tile: {
    flexGrow: 1,
    flexBasis: '45%',
    minWidth: 140,
    alignItems: 'center',
    gap: space.xs,
    paddingVertical: space.lg,
    borderRadius: radius.lg,
    backgroundColor: colors.bg.surfaceSunken,
  },
  loadingRows: {
    gap: space.md,
  },
  quote: {
    marginTop: space.xl,
    padding: space.lg,
    borderRadius: radius.lg,
    backgroundColor: colors.bg.brandSubtle,
    gap: space.xs,
  },
  cta: {
    marginTop: space.xl,
  },
});
